import { Icon, MUIButton, MUIList, MUITextField } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { StatusCodes } from "http-status-codes"
import { useCallback, useContext, useEffect, useState } from "react"
import { Controller, ControllerRenderProps, useForm } from "react-hook-form"
import { useSearchParams } from "react-router-dom"
import { ToastContainer, toast } from "react-toastify"
import { z } from "zod"
import abbLogo from "../../../assets/logos/abb-logo.png"
import { digitalManagerApi } from "../../../services/api"
import { DiMaProgress } from "../../components/DiMaProgress"
import { UserContext } from "../../context/UserContext"
import { ControllerRendererProps } from "../../types/reactHookForm"

const validationSchema = z.object({
  mail: z.string().email("Invalid email").min(1, "Email is required"),
  activationCode: z
    .string()
    .regex(
      /^[0-9A-F]{16}$/,
      "Activation Code must be exactly 16 characters long and contain only hexadecimal characters (0-9, A-F)"
    ),
})

export function GatewayResetPassword() {
  const [searchParams] = useSearchParams()
  const requestId = searchParams.get("requestId") ?? undefined
  const serialNumber = searchParams.get("serialNumber") ?? undefined
  const [isResetRequestCompleted, setIsResetRequestCompleted] = useState<boolean>(false)
  const { user, isLoadingUser } = useContext(UserContext)

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isValid },
  } = useForm<{ mail: string; activationCode: string }>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
  })

  useEffect(() => {
    reset({
      mail: user?.username ?? "",
      activationCode: "",
    })
  }, [user?.username, reset])

  const onSubmit = async (values: { mail: string; activationCode: string }) => {
    try {
      const res = await digitalManagerApi.get(
        `/gateway/resetPassword?requestId=${requestId}&serialNumber=${serialNumber}&activationCode=${values.activationCode}&customerMail=${values.mail}`
      )
      if (res.status === StatusCodes.OK) {
        setIsResetRequestCompleted(true)
      }
    } catch {
      toast.error("Cannot send reset password request")
    }
  }

  const handleMailChange = useCallback(
    (field: ControllerRenderProps<{ mail: string; activationCode: string }, "mail">) =>
      (value: string) =>
        field.onChange(value),
    []
  )

  const renderMailTextField = useCallback(
    ({
      field,
    }: ControllerRendererProps<{ mail: string; activationCode: string }, "mail">) => (
      <div style={{ marginTop: "2%", width: "100%" }}>
        <MUITextField
          id="mail"
          type="text"
          size={32}
          name={field.name}
          value={field.value ?? ""}
          onChange={handleMailChange(field)}
          onBlur={field.onBlur}
          isDisabled
          multiline
          rows={4}
          errors={
            typeof errors.mail?.message === "string"
              ? {
                  mail: {
                    message:
                      errors.mail.message.charAt(0).toUpperCase() +
                      errors.mail.message.slice(1),
                  },
                }
              : undefined
          }
        />
      </div>
    ),
    [errors.mail, handleMailChange]
  )

  const handleActivationCodeChange = useCallback(
    (
        field: ControllerRenderProps<
          { mail: string; activationCode: string },
          "activationCode"
        >
      ) =>
      (value: string) =>
        field.onChange(value.toUpperCase()),
    []
  )

  const renderTextFieldActivationCode = useCallback(
    ({
      field,
    }: ControllerRendererProps<
      { mail: string; activationCode: string },
      "activationCode"
    >) => (
      <div style={{ marginTop: "2%", width: "100%" }}>
        <MUITextField
          id="activationCode"
          type="text"
          size={32}
          name={field.name}
          value={field.value ?? ""}
          onChange={handleActivationCodeChange(field)}
          onBlur={field.onBlur}
          placeholder="Insert the Activation Code of the gateway"
          multiline
          rows={5}
          errors={
            typeof errors.activationCode?.message === "string"
              ? {
                  activationCode: {
                    message:
                      errors.activationCode.message.charAt(0).toUpperCase() +
                      errors.activationCode.message.slice(1),
                  },
                }
              : undefined
          }
        />
      </div>
    ),
    [errors.activationCode, handleActivationCodeChange]
  )

  if (isLoadingUser || !user || !requestId || !serialNumber) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <DiMaProgress />
      </div>
    )
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "10%",
      }}
    >
      <ToastContainer
        position="bottom-right"
        autoClose={5000}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnHover={false}
        pauseOnFocusLoss={false}
        draggable
        theme="colored"
      />
      <img src={abbLogo} alt="ABB Company Logo" />
      <form onSubmit={handleSubmit(onSubmit)}>
        <MUIList
          listItems={[
            {
              id: "0",
              title:
                "If you have lost your administrator password of the gateway, please fill out the form below to initiate a password reset request.",
              description: "",
            },
            {
              id: "1",
              title:
                "After submitting the form, you will receive an email within the next three working days containing an attached file to upload to your gateway.",
              description: "",
            },
          ]}
          size={32}
        />

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: 2,
          }}
        >
          <div style={{ flex: "0 0 65%" }}>
            <Controller name="mail" control={control} render={renderMailTextField} />
          </div>
          <div style={{ flex: "0 0 65%" }}>
            <Controller
              name="activationCode"
              control={control}
              render={renderTextFieldActivationCode}
            />
          </div>
        </div>

        {isResetRequestCompleted ? (
          <Icon iconName="icon_abb_dias icon-abb_completed-check" size={32} />
        ) : (
          <MUIButton
            label="RAISE RESET PASSWORD REQUEST"
            iconName="icon_abb_dias icon-abb_reset"
            variant="primary"
            size={40}
            isLoading={isSubmitting}
            disabled={!isValid || isSubmitting}
            onClickButton={handleSubmit(onSubmit)}
          />
        )}
      </form>
    </div>
  )
}
